import { useState, useEffect, useRef, useCallback } from 'react';

interface UseWebSocketOptions {
  url: string;
  onMessage?: (data: any) => void;
  onOpen?: () => void;
  onError?: (error: any) => void;
  onClose?: () => void;
  reconnectInterval?: number;
  reconnectLimit?: number;
}

export const useWebSocket = (options: UseWebSocketOptions) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const optionsRef = useRef(options);
  const isConnectedRef = useRef(false); // 클로저 문제 방지를 위한 ref

  // options를 ref로 저장하여 의존성 문제 해결
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const connectWebSocket = useCallback(() => {
    const url = optionsRef.current.url;
    console.log('🔌 WebSocket 연결 시도:', url);
    
    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

    ws.onopen = () => {
      console.log('🔌 WebSocket 연결 성공:', optionsRef.current.url);
      isConnectedRef.current = true;
      setIsConnected(true);
      setError(null);
      reconnectCountRef.current = 0; // 연결 성공 시 재연결 카운트 리셋
      optionsRef.current.onOpen?.();
    };

    ws.onmessage = (event) => {
      try {
        // 메시지를 받으면 연결 상태를 true로 설정 (연결이 끊어진 후 재연결된 경우 대비)
        if (!isConnectedRef.current) {
          console.log('🔌 WebSocket 메시지 수신으로 연결 상태 확인됨');
          isConnectedRef.current = true;
          setIsConnected(true);
          setError(null);
        }
        const data = JSON.parse(event.data);
        optionsRef.current.onMessage?.(data);
      } catch (err) {
        console.error('WebSocket 메시지 파싱 오류:', err);
        setError('메시지 파싱 오류');
      }
    };

    ws.onclose = (event) => {
      // WebSocket 종료 코드 설명
      const closeCodeMessages: Record<number, string> = {
        1000: '정상 종료',
        1001: '엔드포인트가 사라짐',
        1002: '프로토콜 오류',
        1003: '지원하지 않는 데이터 타입',
        1006: '비정상 종료 (연결이 닫혔지만 종료 핸드셰이크 없음)',
        1007: '데이터 타입 오류',
        1008: '정책 위반',
        1009: '메시지가 너무 큼',
        1010: '확장 협상 실패',
        1011: '서버 오류',
        1012: '서비스 재시작',
        1013: '일시적인 서비스 오류',
        1014: '서버가 게이트웨이를 통해 응답하지 않음',
        1015: 'TLS 핸드셰이크 실패',
      };
      
      const codeMessage = closeCodeMessages[event.code] || `알 수 없는 코드 (${event.code})`;
      
      console.error('🔌 WebSocket 연결 종료:', {
        code: event.code,
        codeMessage: codeMessage,
        reason: event.reason || '(이유 없음)',
        wasClean: event.wasClean,
        url: optionsRef.current.url,
        readyState: ws.readyState,
      });
      
      // 재연결 로직 (reconnectLimit이 0보다 큰 경우에만)
      const reconnectLimit = optionsRef.current.reconnectLimit ?? 5; // 기본 5번
      
      if (event.code !== 1000 && reconnectLimit > 0) { // 정상 종료가 아니고 재연결이 허용된 경우
        // 재연결 중이면 연결 상태를 유지 (재연결이 진행 중임을 나타냄)
        // 재연결이 실패하면 나중에 상태가 false로 변경됨
        let errorMessage = `WebSocket 연결 종료 (코드: ${event.code})`;
        if (event.reason) {
          errorMessage += ` - ${event.reason}`;
        }
        setError(errorMessage);
        
        // 재연결 로직
        const reconnectInterval = optionsRef.current.reconnectInterval || 5000; // 기본 5초
        
        if (reconnectCountRef.current < reconnectLimit) {
          reconnectCountRef.current++;
          console.log(`🔄 WebSocket 재연결 시도 ${reconnectCountRef.current}/${reconnectLimit} (${reconnectInterval}ms 후)`);
          
          // 재연결 시도 중에는 연결 상태를 false로 설정
          // 재연결 성공 시 onopen에서 true로 변경됨
          isConnectedRef.current = false;
          setIsConnected(false);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, reconnectInterval);
        } else {
          console.log('❌ WebSocket 재연결 한도 초과');
          setError('WebSocket 재연결 한도 초과');
          isConnectedRef.current = false;
          setIsConnected(false);
        }
      } else {
        // 정상 종료이거나 재연결이 비활성화된 경우
        isConnectedRef.current = false;
        setIsConnected(false);
        if (reconnectLimit === 0) {
          console.log('✅ WebSocket 연결 종료 (재연결 비활성화)');
        } else {
          console.log('✅ WebSocket 정상 종료');
        }
      }
      optionsRef.current.onClose?.();
    };

    ws.onerror = (error) => {
      const errorDetails = {
        error,
        errorType: error.type || 'unknown',
        errorTarget: (error.target as WebSocket)?.url || optionsRef.current.url,
        readyState: ws.readyState,
        readyStateText: ws.readyState === 0 ? 'CONNECTING' : ws.readyState === 1 ? 'OPEN' : ws.readyState === 2 ? 'CLOSING' : 'CLOSED',
        protocol: ws.protocol || '(없음)',
        extensions: ws.extensions || '(없음)',
        url: optionsRef.current.url,
      };
      
      console.error('❌ WebSocket 에러 상세:', errorDetails);
      
      let errorMessage = `WebSocket 연결 실패`;
      if (ws.readyState === WebSocket.CLOSED) {
        errorMessage += ' (연결이 닫혔습니다)';
      } else if (ws.readyState === WebSocket.CONNECTING) {
        errorMessage += ' (연결 시도 중 오류 발생)';
      }
      errorMessage += `: ${optionsRef.current.url}`;
      
      setError(errorMessage);
      optionsRef.current.onError?.(error);
    };
    } catch (err) {
      console.error('❌ WebSocket 생성 실패:', err);
      setError(`WebSocket 생성 실패: ${err instanceof Error ? err.message : String(err)}`);
      optionsRef.current.onError?.(err);
    }
  }, []); // 의존성 배열을 비워서 한 번만 생성

  useEffect(() => {
    // URL이 없으면 연결하지 않음
    if (!optionsRef.current.url) {
      // 기존 연결이 있으면 종료
      if (wsRef.current) {
        console.log('🔌 WebSocket URL이 비어있어 연결 종료');
        wsRef.current.close(1000, 'URL이 비어있음'); // 정상 종료 코드로 종료하여 재연결 방지
        wsRef.current = null;
        isConnectedRef.current = false;
        setIsConnected(false);
      }
      return;
    }
    
      // 기존 연결이 있으면 먼저 종료
      if (wsRef.current) {
        wsRef.current.close();
        isConnectedRef.current = false;
        setIsConnected(false);
      }
      
      // 재연결 카운트 리셋
      reconnectCountRef.current = 0;
      
      connectWebSocket();

    return () => {
      // 재연결 타이머 정리
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      // WebSocket 연결 종료
      if (wsRef.current) {
        console.log('🔌 WebSocket cleanup - 연결 종료');
        wsRef.current.close(1000, '컴포넌트 언마운트'); // 정상 종료 코드로 종료하여 재연결 방지
        wsRef.current = null;
        isConnectedRef.current = false;
        setIsConnected(false);
      }
    };
  }, [connectWebSocket, options.url]); // URL 변경 시 재연결

  const sendMessage = (message: any) => {
    if (wsRef.current && isConnected) {
      wsRef.current.send(JSON.stringify(message));
    }
  };

  return { isConnected, error, sendMessage };
};
